import { TicketMutationEvent } from "@jugl-web/domain-resources/tickets/types/TicketMutationEvent";
import {
  CRMChannel,
  WhatsappMessageModel,
  instagramMessages$,
  twilioMessages$,
  webChatBotMessages$,
} from "@jugl-web/rest-api";
import { EmailConversationDto } from "@jugl-web/rest-api/integrations/models/EmailConversation";
import {
  EmailMessageModel,
  transformEmailMessageDtoToModel,
} from "@jugl-web/rest-api/integrations/models/EmailMessage";
import { InstagramMessageModel } from "@jugl-web/rest-api/integrations/models/InstagramMessage/InstagramMessageModel";
import {
  TwilioMessageDto,
  TwilioMessageModel,
  transformTwilioMessageDtoToModel,
} from "@jugl-web/rest-api/integrations/models/TwilioMessage";
import { WebChatBotMessageModel } from "@jugl-web/rest-api/integrations/models/WebChatBotMessage";
import { WebChatBotMessageDto } from "@jugl-web/rest-api/integrations/models/WebChatBotMessage/WebChatBotMessageDto";
import { transformWebChatBotMessageDtoToModel } from "@jugl-web/rest-api/integrations/models/WebChatBotMessage/utils";
import { useToast } from "@jugl-web/utils";
import { useLocalStorage } from "@jugl-web/utils/hooks/useStorage";
import { CRM_INBOX_PREFERENCES_KEY } from "@jugl-web/utils/storage";
import { useCRMUnreadIndicators } from "@web-src/modules/client-support/providers/CRMUnreadIndicatorsProvider";
import { useEntityProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntityProvider";
import { useNavigation } from "@web-src/modules/navigation/hooks/useNavigation";
import { PeopleInfoSidebar } from "@web-src/modules/people/components/PeopleInfoSidebar";
import usePhoenixSocket from "@web-src/modules/socket/providers/usePhoenixSocket";
import debounce from "lodash/debounce";
import { Channel } from "phoenix";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Subject } from "rxjs";
import { getChatSecondsToExpiration } from "../../components/ClientSupportChat/utils/getChatSecondsToExpiration";
import { ConversationSidePanelTab } from "../../components/ConversationSidePanel/types";
import { useManageAccountsDrawer } from "../../components/ManageAccountsDrawer/providers/ManageAccountsDrawerContext";
import {
  ChannelListItem,
  ClientSupportView,
  ConversationModel,
  CreateTicketSidebarState,
  InboxPreferencesByEntityId,
} from "../../types";
import {
  getChannelFromView,
  isCRMChannel,
  isClientSupportViewsEqual,
  isTicketFilterId,
  resolveInitialClientSupportView,
  resolveViewFromEntityPreferences,
} from "../../utils";
import { isConversationContactBlocked } from "../../utils/isConversationContactBlocked";
import { useEmailAccount } from "../EmailAccountProvider/useEmailAccount";
import { useInstagramAccount } from "../InstagramAccountProvider/useInstagramAccount";
import { useWebChatBotAccount } from "../WebChatBotAccountProvider/useWebChatBotAccount";
import { useWhatsappAccount } from "../WhatsappAccountProvider/useWhatsappAccount";
import {
  ClientSupportContext,
  ClientSupportPageContextValue,
} from "./ClientSupportPageContext";
import { useTicketDetailsSidebarState } from "./useTicketDetailsSidebarState";

type ReplyMessagesMap = Record<
  string,
  | WhatsappMessageModel
  | InstagramMessageModel
  | WebChatBotMessageModel
  | TwilioMessageModel
  | EmailMessageModel
  | undefined
>;

export const ClientSupportPageProvider: FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { channel: urlChannel, filter: urlFilter } = useParams<string>();
  const [searchParams] = useSearchParams();

  const { entityId } = useEntityProvider();

  const [selectedConversation, setSelectedConversation] =
    useState<ConversationModel>();

  const [selectedView, setSelectedView] = useState<ClientSupportView>(() =>
    resolveInitialClientSupportView({
      urlChannel,
      urlFilter,
      entityId: entityId ?? undefined,
      availableChannels: [],
    })
  );

  const [inboxPreferences, setInboxPreferences] =
    useLocalStorage<InboxPreferencesByEntityId>(CRM_INBOX_PREFERENCES_KEY, {});

  const [createTicketSidebarState, setCreateTicketSidebarState] =
    useState<CreateTicketSidebarState>({ isOpen: false });

  const {
    ticketDetailsSidebarState,
    openTicketDetailsSidebar,
    closeTicketDetailsSidebar,
  } = useTicketDetailsSidebarState();

  const [replyMessages, setReplyMessages] = useState<ReplyMessagesMap>({});
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [openedUserProfile, setOpenedUserProfile] = useState<string>();
  const [isConversationSidePanelOpen, setIsConversationSidePanelOpen] =
    useState(false);
  const [conversationSidePanelVariant, setConversationSidePanelVariant] =
    useState<ConversationSidePanelTab>();
  const [chatSecondsLeft, setChatSecondsLeft] = useState(0);

  const openConversationSidePanel = useCallback(
    (params?: { variant?: ConversationSidePanelTab }) => {
      setConversationSidePanelVariant(
        params?.variant ?? ConversationSidePanelTab.customer
      );
      setIsConversationSidePanelOpen(true);
    },
    []
  );

  const closeConversationSidePanel = useCallback(() => {
    setIsConversationSidePanelOpen(false);
  }, []);

  const isContactBlocked = useMemo(
    () => isConversationContactBlocked(selectedConversation),
    [selectedConversation]
  );

  const selectedChannel = useMemo(
    () => getChannelFromView(selectedView) ?? CRMChannel.whatsapp,
    [selectedView]
  );

  useEffect(() => {
    closeConversationSidePanel();
  }, [closeConversationSidePanel, selectedConversation?.conv_id]);

  const isExpirableChannel =
    selectedView.kind === "channel" &&
    (selectedView.channel === CRMChannel.whatsapp ||
      selectedView.channel === CRMChannel.instagram);

  const isChatExpired = useMemo(
    () => chatSecondsLeft <= 0 && isExpirableChannel,
    [chatSecondsLeft, isExpirableChannel]
  );

  // Recompute the chat window countdown when the active conversation changes.
  // Only whatsapp/instagram chats expire; email conversations also carry
  // `since`, but it must not start the expiration countdown.
  useEffect(() => {
    if (
      selectedConversation &&
      "since" in selectedConversation &&
      isExpirableChannel
    ) {
      setChatSecondsLeft(
        getChatSecondsToExpiration(selectedConversation.since)
      );
    } else {
      setChatSecondsLeft(0);
    }
  }, [selectedConversation, isExpirableChannel]);

  // Decrement the chat window countdown every second while a timed conversation is open.
  useEffect(() => {
    if (
      !selectedConversation ||
      !("since" in selectedConversation) ||
      !isExpirableChannel
    ) {
      return undefined;
    }

    const timer = setInterval(() => {
      setChatSecondsLeft((prev) => (prev > 0 ? prev - 1 : prev));
    }, 1000);

    return () => clearInterval(timer);
  }, [selectedConversation, isExpirableChannel]);

  const { socket } = usePhoenixSocket();
  const $incomingWhatsappMessages = useRef<
    ClientSupportPageContextValue["incomingWhatsappMessages$"]
  >(new Subject());
  const $incomingInstagramMessages = useRef<
    ClientSupportPageContextValue["incomingInstagramMessages$"]
  >(new Subject());
  const $incomingWebMessages = useRef<
    ClientSupportPageContextValue["incomingWebMessages$"]
  >(new Subject());
  const $incomingTwilioMessages = useRef<
    ClientSupportPageContextValue["incomingTwilioMessages$"]
  >(new Subject());
  const $incomingEmailMessages = useRef<
    ClientSupportPageContextValue["incomingEmailMessages$"]
  >(new Subject());
  const chatbotChannelRef = useRef<Channel | null>(null);
  const { toast } = useToast();
  const { navigateToPage } = useNavigation();
  const { crmConfig, isCRMConfigLoading } = useManageAccountsDrawer();
  const { whatsappAgent } = useWhatsappAccount();
  const { instagramAccount } = useInstagramAccount();
  const { webChatBotAccount } = useWebChatBotAccount();
  const { emailAccount } = useEmailAccount();
  const { refetch: refetchUnreadCounts } = useCRMUnreadIndicators();

  const openCreateTicketSidebar = useCallback(
    (initialFormValues?: CreateTicketSidebarState["initialFormValues"]) => {
      setCreateTicketSidebarState({ isOpen: true, initialFormValues });
    },
    []
  );

  const closeCreateTicketSidebar = useCallback(() => {
    setCreateTicketSidebarState((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const selectView = useCallback(
    (view: ClientSupportView) => {
      if (isClientSupportViewsEqual(selectedView, view)) {
        return;
      }

      if (isSendingMessage) {
        toast(
          "Please wait until the message is sent before switching conversations",
          {
            variant: "warning",
          }
        );
        return;
      }

      setSelectedView(view);
      setSelectedConversation(undefined);

      setInboxPreferences((prev) => {
        if (!entityId) {
          return prev;
        }

        return {
          ...prev,
          [entityId]: { lastView: view },
        };
      });

      if (view.kind === "channel") {
        navigateToPage(
          "clientSupportChannel",
          { channel: view.channel },
          { replacePathOnly: true }
        );
        return;
      }

      navigateToPage(
        "clientSupportTickets",
        { filter: view.filter },
        { replacePathOnly: true }
      );
    },
    [
      entityId,
      isSendingMessage,
      navigateToPage,
      selectedView,
      setInboxPreferences,
      toast,
    ]
  );

  const selectChannel = useCallback(
    (channel: CRMChannel) => {
      selectView({ kind: "channel", channel });
    },
    [selectView]
  );

  const selectConversation = useCallback(
    (conversation?: ConversationModel, auto?: boolean) => {
      if (isSendingMessage && !auto) {
        toast(
          "Please wait until the message is sent before switching conversations",
          {
            variant: "warning",
          }
        );
        return;
      }

      if (selectedView.kind !== "channel") {
        return;
      }

      setSelectedConversation(conversation);

      if (conversation) {
        navigateToPage(
          "clientSupportActiveConversation",
          {
            channel: selectedView.channel,
            activeConversation: conversation.conv_id,
          },
          { replacePathOnly: true }
        );
        return;
      }

      navigateToPage(
        "clientSupportChannel",
        { channel: selectedView.channel },
        { replacePathOnly: true }
      );
    },
    [isSendingMessage, navigateToPage, selectedView, toast]
  );

  const setReplyMessage = useCallback(
    (
      conversationId: string,
      message?:
        | WhatsappMessageModel
        | InstagramMessageModel
        | WebChatBotMessageModel
        | TwilioMessageModel
        | EmailMessageModel
    ) => {
      setReplyMessages((prev) => ({
        ...prev,
        [conversationId]: message,
      }));
    },
    []
  );

  const refetchTrigger$ = useMemo(() => new Subject<void>(), []);
  const ticketMutations$ = useMemo(
    () => new Subject<TicketMutationEvent>(),
    []
  );

  // sidebar unread counters: re-sync from the server shortly after socket
  // messages arrive on any channel
  const debouncedRefetchUnreadCounts = useMemo(
    () => debounce(() => refetchUnreadCounts(), 2000),
    [refetchUnreadCounts]
  );

  useEffect(
    () => () => debouncedRefetchUnreadCounts.cancel(),
    [debouncedRefetchUnreadCounts]
  );

  const availableChannels: ChannelListItem[] = useMemo(() => {
    if (isCRMConfigLoading) return [];

    const channels: ChannelListItem[] = [];

    // Add Twilio channel
    if (crmConfig?.twilio?.id) {
      channels.push({
        id: CRMChannel.twilio,
      });
    }

    if (webChatBotAccount) {
      channels.unshift({
        id: CRMChannel.chatbot,
        name: webChatBotAccount.name,
      });
    }

    if (instagramAccount) {
      channels.unshift({
        id: CRMChannel.instagram,
        name: instagramAccount.name,
      });
    }

    if (whatsappAgent) {
      channels.unshift({
        id: CRMChannel.whatsapp,
        name: whatsappAgent.name,
      });
    }

    if (emailAccount) {
      channels.unshift({
        id: CRMChannel.email,
      });
    }

    return channels;
  }, [
    isCRMConfigLoading,
    crmConfig?.twilio?.id,
    webChatBotAccount,
    instagramAccount,
    whatsappAgent,
    emailAccount,
  ]);

  const hasOAuthCallback = searchParams.has("code");

  const isBareInboxPath = !urlChannel && !urlFilter && !hasOAuthCallback;

  // Keep selectedView in sync with explicit channel or tickets segments in the URL.
  useEffect(() => {
    if (urlFilter && isTicketFilterId(urlFilter)) {
      const ticketsView: ClientSupportView = {
        kind: "tickets",
        filter: urlFilter,
      };

      setSelectedView((previousView) =>
        isClientSupportViewsEqual(previousView, ticketsView)
          ? previousView
          : ticketsView
      );
      return;
    }

    if (urlChannel && isCRMChannel(urlChannel)) {
      const channelView: ClientSupportView = {
        kind: "channel",
        channel: urlChannel,
      };

      setSelectedView((previousView) =>
        isClientSupportViewsEqual(previousView, channelView)
          ? previousView
          : channelView
      );
    }
  }, [urlChannel, urlFilter]);

  // On bare /client-support, restore the entity's last inbox view and replace the URL to match.
  useEffect(() => {
    if (!isBareInboxPath || !entityId) {
      return;
    }

    const savedView = resolveViewFromEntityPreferences(
      inboxPreferences[entityId],
      availableChannels
    );

    if (!savedView) {
      return;
    }

    setSelectedView((previousView) =>
      isClientSupportViewsEqual(previousView, savedView)
        ? previousView
        : savedView
    );

    if (savedView.kind === "channel") {
      navigateToPage(
        "clientSupportChannel",
        { channel: savedView.channel },
        { replace: true }
      );
      return;
    }

    navigateToPage(
      "clientSupportTickets",
      { filter: savedView.filter },
      { replace: true }
    );
  }, [
    availableChannels,
    entityId,
    inboxPreferences,
    isBareInboxPath,
    navigateToPage,
  ]);

  // If the selected channel was disconnected, switch to the first still-available channel.
  useEffect(() => {
    if (selectedView.kind !== "channel" || !availableChannels.length) {
      return;
    }

    const isCurrentChannelAvailable = availableChannels.some(
      (channel) => channel.id === selectedView.channel
    );

    if (!isCurrentChannelAvailable) {
      const newChannel = availableChannels[0].id;
      selectView({ kind: "channel", channel: newChannel });
    }
  }, [availableChannels, selectView, selectedView]);

  const sendWebMessage = useCallback(
    (params: { conv_id: string; conv_name: string; text: string }) => {
      const timestamp = Date.now();

      // Send via socket
      chatbotChannelRef.current?.push("send_chatbot_message", {
        conv_id: params.conv_id,
        conv_name: params.conv_name,
        payload: {
          text: params.text,
          type: "text",
          timestamp,
        },
      });
    },
    []
  );

  // Subscribe to CRM Phoenix channels and fan out incoming messages to context subjects.
  useEffect(() => {
    if (!socket) return () => {};

    const whatsappChannel = socket.channel("whatsapp:messages");
    const instagramChannel = socket.channel("instagram:messages");
    const chatbotChannel = socket.channel("chatbot:lobby");
    const twilioChannel = socket.channel("twilio:lobby");
    const emailChannel = socket.channel("email:messages");

    // Store chatbot channel in ref for sendWebMessage
    chatbotChannelRef.current = chatbotChannel;

    whatsappChannel
      .join()
      .receive("ok", () => {
        // TODO: handle ok
      })
      .receive("error", () => {
        // TODO: handle error
      });

    instagramChannel
      .join()
      .receive("ok", () => {
        // TODO: handle okn
      })
      .receive("error", () => {
        // TODO: handle error
      });

    chatbotChannel
      .join()
      .receive("ok", () => {
        // TODO: handle ok
      })
      .receive("error", () => {
        // TODO: handle error
      });

    twilioChannel
      .join()
      .receive("ok", () => {
        // TODO: handle ok
      })
      .receive("error", () => {
        // TODO: handle error
      });

    emailChannel
      .join()
      .receive("ok", () => {
        // TODO: handle ok
      })
      .receive("error", () => {
        // TODO: handle error
      });

    whatsappChannel.on(
      "wa_message",
      ({ message }: { message: WhatsappMessageModel }) => {
        $incomingWhatsappMessages.current.next(message);
        debouncedRefetchUnreadCounts();
      }
    );

    instagramChannel.on(
      "ig_message",
      ({ message }: { message: InstagramMessageModel }) => {
        $incomingInstagramMessages.current.next(message);
        instagramMessages$.next({ type: "append", message });
        debouncedRefetchUnreadCounts();
      }
    );

    chatbotChannel.on(
      "chatbot_message",
      ({ message }: { message: WebChatBotMessageDto }) => {
        const transformedMessage =
          transformWebChatBotMessageDtoToModel(message);

        $incomingWebMessages.current.next(transformedMessage);

        webChatBotMessages$.next({
          type: "append",
          message: transformedMessage,
        });
        debouncedRefetchUnreadCounts();
      }
    );

    twilioChannel.on(
      "twilio_message",
      ({ message }: { message: TwilioMessageDto }) => {
        const transformedMessage = transformTwilioMessageDtoToModel(message);
        $incomingTwilioMessages.current.next(transformedMessage);
        twilioMessages$.next({ type: "append", message: transformedMessage });
        debouncedRefetchUnreadCounts();
      }
    );

    emailChannel.on(
      "email_message",
      ({ message }: { message: EmailConversationDto }) => {
        const transformedMessage = transformEmailMessageDtoToModel(message);

        $incomingEmailMessages.current.next(transformedMessage);
        debouncedRefetchUnreadCounts();
      }
    );

    return () => {
      whatsappChannel.leave();
      instagramChannel.leave();
      chatbotChannel.leave();
      twilioChannel.leave();
      emailChannel.leave();
      chatbotChannelRef.current = null;
    };
  }, [socket, debouncedRefetchUnreadCounts]);

  const value: ClientSupportPageContextValue = useMemo(
    () => ({
      selectedView,
      selectView,
      selectedConversation,
      selectConversation,
      refetchTrigger$,
      ticketMutations$,
      replyMessage: selectedConversation
        ? replyMessages[selectedConversation.conv_id]
        : undefined,
      setReplyMessage,
      incomingWhatsappMessages$: $incomingWhatsappMessages.current,
      incomingInstagramMessages$: $incomingInstagramMessages.current,
      incomingWebMessages$: $incomingWebMessages.current,
      incomingTwilioMessages$: $incomingTwilioMessages.current,
      incomingEmailMessages$: $incomingEmailMessages.current,
      sendWebMessage,
      isSendingMessage,
      setIsSendingMessage,
      openedUserProfile,
      setOpenedUserProfile,
      selectChannel,
      selectedChannel,
      ticketDetailsSidebarState,
      openTicketDetailsSidebar,
      closeTicketDetailsSidebar,
      createTicketSidebarState,
      openCreateTicketSidebar,
      closeCreateTicketSidebar,
      availableChannels,
      isConversationSidePanelOpen,
      conversationSidePanelVariant,
      openConversationSidePanel,
      closeConversationSidePanel,
      isContactBlocked,
      isChatExpired,
      chatSecondsLeft,
    }),
    [
      selectedView,
      selectView,
      selectedConversation,
      selectConversation,
      refetchTrigger$,
      ticketMutations$,
      replyMessages,
      setReplyMessage,
      sendWebMessage,
      isSendingMessage,
      openedUserProfile,
      selectChannel,
      selectedChannel,
      ticketDetailsSidebarState,
      openTicketDetailsSidebar,
      closeTicketDetailsSidebar,
      createTicketSidebarState,
      openCreateTicketSidebar,
      closeCreateTicketSidebar,
      availableChannels,
      isConversationSidePanelOpen,
      conversationSidePanelVariant,
      openConversationSidePanel,
      closeConversationSidePanel,
      isContactBlocked,
      isChatExpired,
      chatSecondsLeft,
    ]
  );

  return (
    <ClientSupportContext.Provider value={value}>
      <PeopleInfoSidebar
        userId={openedUserProfile}
        isOpen={!!openedUserProfile}
        onRequestClose={() => setOpenedUserProfile(undefined)}
      />
      {children}
    </ClientSupportContext.Provider>
  );
};
